import classNames from 'classnames';
import { type ReactNode, useContext } from 'react';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import { eyreLanguageKey } from '@/i18n/utils';
import { searchKeys } from '@/shared/utils/search-parameters';

import { type Panel, resolveLayout, useEyreUiConfig } from './config';
import './global.scss';
import styles from './frame.module.scss';
import { useEyreT } from './strings';

// Logto's server inlines the tenant custom CSS (written for the stock UI); OpenResty strips it when it
// swaps in this UI, this is the fallback for any page served without the swap.
for (const element of document.querySelectorAll('style[data-custom-css]')) {
  element.remove();
}

const languages = [
  { value: 'zh-CN', label: '简体中文' },
  { value: 'en', label: 'English' },
];

const languageLabel = (tag: string) => {
  try {
    return new Intl.DisplayNames([tag], { type: 'language' }).of(tag) ?? tag;
  } catch {
    return tag;
  }
};

/** Shows the language the page is actually rendered in (Logto may auto-detect one we don't list). */
export const LanguageSelect = ({ className }: { readonly className?: string }) => {
  const { language } = useEyreT();
  const listed = languages.find(({ value }) => value.toLowerCase() === language.toLowerCase());
  const current = listed?.value ?? language;
  const options = listed
    ? languages
    : [{ value: language, label: languageLabel(language) }, ...languages];

  return (
    <label className={classNames(styles.language, className)}>
      <select
        value={current}
        aria-label="Language"
        onChange={(event) => {
          const { value } = event.target;
          // Logto keeps ui_locales in sessionStorage for the whole flow, so later pages stay in this language
          sessionStorage.setItem(searchKeys.uiLocales, value);
          localStorage.setItem(eyreLanguageKey, value);
          // Reload instead of switching in place: some Logto fields cache their labels, a reload
          // re-renders every string in the new language (the flow survives a refresh)
          window.location.reload();
        }}
      >
        {options.map(({ value, label }) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M7 10l5 5 5-5z" />
      </svg>
    </label>
  );
};

const useFooterLinks = () => {
  const { experienceSettings } = useContext(PageContext);
  const config = useEyreUiConfig();
  const { t } = useEyreT();

  return [
    { label: t('help'), href: config?.links?.help ?? experienceSettings?.supportWebsiteUrl },
    {
      label: t('privacyShort'),
      href: config?.links?.privacy ?? experienceSettings?.privacyPolicyUrl,
    },
    { label: t('termsShort'), href: config?.links?.terms ?? experienceSettings?.termsOfUseUrl },
  ].filter((link): link is { label: string; href: string } => Boolean(link.href));
};

const FooterLinks = ({ className }: { readonly className?: string }) => {
  const links = useFooterLinks();

  if (links.length === 0) {
    return null;
  }

  return (
    <nav className={className}>
      {links.map(({ label, href }) => (
        <a key={label} href={href} target="_blank" rel="noopener noreferrer">
          {label}
        </a>
      ))}
    </nav>
  );
};

export const BrandMark = ({ className }: { readonly className?: string }) => {
  const { experienceSettings } = useContext(PageContext);
  const { t } = useEyreT();
  const logo = experienceSettings?.branding.logoUrl;

  return (
    <div className={classNames(styles.brand, className)}>
      {logo && <img src={logo} alt="" />}
      <span>{t('brand')}</span>
    </div>
  );
};

const PanelView = ({ panel }: { readonly panel?: Panel }) => {
  if (!panel) {
    return <div className={styles.panelFallback} />;
  }

  switch (panel.type) {
    case 'image': {
      return <img className={styles.panelMedia} src={panel.src} alt={panel.alt ?? ''} />;
    }
    case 'video': {
      return (
        <video
          autoPlay
          muted
          loop
          playsInline
          className={styles.panelMedia}
          src={panel.src}
          poster={panel.poster}
        />
      );
    }
    case 'iframe': {
      // Panel pages are our own static files; they may run scripts but get no access to this origin
      return (
        <iframe
          className={styles.panelMedia}
          src={panel.src}
          title="panel"
          sandbox="allow-scripts allow-popups"
        />
      );
    }
    case 'html': {
      // Static markup from ui.json (our own file); scripts in it do not run
      return (
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: panel.html ?? '' }}
          className={styles.panelHtml}
        />
      );
    }
  }
};

/** Sign-in / register / verification pages: ChatGPT-style column, plain white or split with a left panel. */
export const AuthFrame = ({ children }: { readonly children: ReactNode }) => {
  const config = useEyreUiConfig();
  const { layout, panel } = resolveLayout(config);

  // In split layout the panel carries the brand (like everyinfra.com); only the fallback panel needs it here
  const showBrand = layout !== 'split' || !panel;
  const column = (
    <div className={styles.authSide}>
      {showBrand && <BrandMark className={styles.topBrand} />}
      <main className={styles.column}>{children}</main>
      <footer className={styles.authFooter}>
        <LanguageSelect />
        <FooterLinks className={styles.authLinks} />
      </footer>
    </div>
  );

  if (layout === 'split') {
    return (
      <div className={styles.split}>
        <aside className={styles.panel}>
          <PanelView panel={panel} />
        </aside>
        {column}
      </div>
    );
  }

  return <div className={styles.plain}>{column}</div>;
};

/** Consent page: Google-style wide card with a header bar and a footer under the card. */
export const ConsentFrame = ({ children }: { readonly children: ReactNode }) => {
  const { experienceSettings } = useContext(PageContext);
  const { t } = useEyreT();
  const logo = experienceSettings?.branding.logoUrl;

  return (
    <div className={styles.consentPage}>
      <div className={styles.consentWrap}>
        <div className={styles.card}>
          <header className={styles.cardHeader}>
            {logo && <img src={logo} alt="" />}
            <span>{t('headerConsent')}</span>
          </header>
          <main className={styles.cardBody}>{children}</main>
        </div>
        <footer className={styles.consentFooter}>
          <LanguageSelect />
          <FooterLinks className={styles.consentLinks} />
        </footer>
      </div>
    </div>
  );
};

/** Centered logo above the title on phone widths (ChatGPT app); hidden on wider screens via CSS. */
export const MobileLogo = () => {
  const { experienceSettings } = useContext(PageContext);
  const logo = experienceSettings?.branding.logoUrl;

  return logo ? <img className={styles.mobileLogo} src={logo} alt="" /> : null;
};
